import type { stacks, users } from "@dropbox/api-v2-client";
import type { AvatarSizes } from "@dropbox/dig-components/avatar";
import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { Menu } from "@dropbox/dig-components/menu";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { MoreHorizontalLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { useAllStacksAugustRevisionEnabled } from "@mirage/august-revision-hook";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { DashFacepile } from "@mirage/mosaics/DCLWrappers/DashFacepile";
import { StacksLoadingContainer } from "@mirage/mosaics/StacksLoading/StacksLoadingContainer";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { useLastViewedStackInfoWithAtom } from "@mirage/service-recent-content/hooks/useLastViewedStackInfo";
import { useIsPublicSharingAllowed } from "@mirage/service-stack-admin-settings/hooks";
import { createStack, createStackItemsBatch } from "@mirage/service-stacks";
import {
  asShortcut,
  stackDerivePAPProps,
  stackGetShareId,
  stackItemGetName,
} from "@mirage/service-stacks/service/utils";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { getDefaultColorIndex } from "@mirage/stacks/themes";
import i18n from "@mirage/translations";
import type { WorkingSetCardData } from "@mirage/working-set";
import { useAtomValue } from "jotai";
import orderBy from "lodash/orderBy";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useMemo, useState } from "react";
import { EmbeddedActions } from "../AllStacksList/EmbeddedActions";
import Actions from "../FullScreenStack/Actions";
import {
  useDisableStackSuggestions,
  useExtensionSuggestedStacks,
  useSortedStacks,
  useSortedStacksWithItemsBySortOptionWithLastViewedStackInfo,
} from "../hooks";
import {
  accessLevelIconSrcForMemberCount,
  accessLevelTitleForAccessLevel,
} from "../ShareModal/Utils";
import { StacksBanner } from "../StacksBanner/StacksBanner";
import { getStackIconUrl } from "../utils";
import {
  EMOJI_CLEARED_SENTINEL_VALUE,
  membersFromStack,
  stackIsPinned,
} from "../utils/helpers";
import styles from "./AllStacks.module.css";
import AllStacksGrid from "./AllStacksGrid";
import { AllStacksV2 } from "./AllStacksV2";
import { stackSearchStacksAtom } from "./atoms";
import { useStackPagePreferences } from "./useStackPagePreferences";

export const AllStacks = ({
  onSelectStack,
}: {
  onSelectStack: (stack: stacks.Stack, numOfLinks: number) => void;
}) => {
  const currentAccount = useDropboxAccount();

  const {
    stackPageSortPreference: sortPreference,
    stackPageFilterPreference: filterPreference,
  } = useStackPagePreferences();
  const sortedStacks = useSortedStacks();
  const suggestedStacks = useExtensionSuggestedStacks();
  const lastViewedStackInfo = useLastViewedStackInfoWithAtom();
  const { stacks: filteredSortedStacks, stackItemsMap } =
    useSortedStacksWithItemsBySortOptionWithLastViewedStackInfo(
      sortPreference,
      filterPreference,
      lastViewedStackInfo,
    );
  const searchResultStacks = useAtomValue(stackSearchStacksAtom);
  const isPublicSharingAllowed = useIsPublicSharingAllowed();

  const [menuOpenForStackId, setMenuOpenForStackId] = useState<string>();

  const augustRevision = useAllStacksAugustRevisionEnabled();

  const convertStacksToWorkingSetCardData = useCallback(
    (stacks: stacks.Stack[]) => {
      return stacks.map((stack: stacks.Stack) => {
        return convertStackToWorkingSetCardData(
          stack,
          stacks,
          onSelectStack,
          stackItemsMap,
          menuOpenForStackId,
          currentAccount,
          augustRevision,
          isPublicSharingAllowed,
          setMenuOpenForStackId,
        );
      });
    },
    [
      augustRevision,
      currentAccount,
      isPublicSharingAllowed,
      menuOpenForStackId,
      onSelectStack,
      stackItemsMap,
    ],
  );

  const [workingSetCardData, pinnedSetCardData, suggestedCardData]: [
    WorkingSetCardData[],
    WorkingSetCardData[],
    WorkingSetCardData[],
  ] = useMemo((): [
    WorkingSetCardData[],
    WorkingSetCardData[],
    WorkingSetCardData[],
  ] => {
    const stacksToList = searchResultStacks ?? filteredSortedStacks ?? [];
    const pinnedStacks = sortedStacks?.filter(stackIsPinned);
    const stackSuggestions = suggestedStacks.filter(
      (s) =>
        !sortedStacks?.some(
          // Do not show suggestion if a stack with the same name already exists
          (stack) => stack.stack_data?.name === s.stack_data?.name,
        ),
    );
    return [
      convertStacksToWorkingSetCardData(stacksToList),
      convertStacksToWorkingSetCardData(pinnedStacks ?? []),
      stackSuggestions.map(convertStackSuggestionToWorkingSetCardData),
    ];
  }, [
    convertStacksToWorkingSetCardData,
    filteredSortedStacks,
    searchResultStacks,
    sortedStacks,
    suggestedStacks,
  ]);

  return (
    <div className={styles.wrapper}>
      <StacksBanner className={styles.featureBanner} />
      <StacksLoadingContainer loading={sortPreference === undefined}>
        {augustRevision ? (
          <AllStacksV2
            workingSetCardData={workingSetCardData}
            pinnedSetCardData={pinnedSetCardData}
            suggestedCardData={suggestedCardData}
            filterPreference={filterPreference}
            menuOpenForStackId={menuOpenForStackId}
          />
        ) : (
          <AllStacksGrid
            workingSetCardData={workingSetCardData}
            filterPreference={filterPreference}
          />
        )}
      </StacksLoadingContainer>
    </div>
  );
};

export const convertStackToWorkingSetCardData = (
  stack: stacks.Stack,
  stacks: stacks.Stack[],
  onSelectStack: (stack: stacks.Stack, numOfLinks: number) => void,
  stackItemsMap?: { [nsid: string]: stacks.StackItem[] },
  menuOpenForStackId?: string,
  currentAccount?: users.FullAccount,
  augustRevision?: boolean,
  isPublicSharingAllowed?: boolean,
  setMenuOpenForStackId?: Dispatch<SetStateAction<string | undefined>>,
  chatSources?: ChatSource[],
) => {
  const members = membersFromStack(stack);
  const stackItems = stackItemsMap?.[stack.namespace_id ?? ""] ?? [];

  return {
    stack,
    namespaceId: stack.namespace_id ?? "",
    id: stackGetShareId(stack) ?? "",
    title: stack.stack_data?.name ?? "",
    lastModifiedMs: (stack.last_modified_time_utc_sec ?? 0) * 1000,
    maxHlcMs: (stack.max_hlc_micros ?? 0) / 1000,
    colorIndex: stack.stack_data?.color_index ?? getDefaultColorIndex(),
    emoji: stack.stack_data?.emoji ?? EMOJI_CLEARED_SENTINEL_VALUE,
    logoUrl: getStackIconUrl(stack),
    isPinned: stackIsPinned(stack),
    pinnedSortKey: stack.user_data?.pinned_sort_key,
    isCompanyPublishedStack: !!stack?.publish_data?.is_published,
    isPublicStack:
      !!isPublicSharingAllowed &&
      stack.sharing_data?.shared_link?.access_level?.[".tag"] === "public",
    menuIsOpen: menuOpenForStackId === stack.namespace_id,
    debugData: {
      pinnedSortKey: stack.user_data?.pinned_sort_key,
      pinnedMs: stack.user_data?.pinned_ms,
      maxHlc: stack.max_hlc_micros,
    },
    memberCount: members.length,
    accessLevelIcon: accessLevelIconSrcForMemberCount(
      stack.sharing_data?.members?.length ?? 0,
      stack.sharing_data?.shared_link?.access_level,
      stack.publish_data?.is_published,
      isPublicSharingAllowed,
    ),
    accessLevel: accessLevelTitleForAccessLevel(
      stack.publish_data,
      stack?.sharing_data?.shared_link?.access_level,
      stack?.sharing_data?.members?.length ?? 0,
      isPublicSharingAllowed,
    ),
    links: orderBy(
      stackItems.map(asShortcut).filter(isDefined),
      [(item) => item.last_modified_time_utc_sec],
      ["desc"],
    ).map((stackItem) => {
      const { url, api_file_id } = stackItem;
      return {
        url: url ?? "",
        id: api_file_id ?? "",
        name: stackItemGetName(stackItem, stacks),
      };
    }),
    // This use case is probably fine: Due to naming convention this
    // cannot be directly rendered as a react component; instead it must
    // be invoked and the return value is rendered.
    // eslint-disable-next-line react/no-unstable-nested-components
    getMembersComponent: (
      size: AvatarSizes = "medium",
      isInteractive: boolean = true,
    ) => (
      <DashFacepile
        maxMembersToShow={3}
        members={members}
        size={size}
        isInteractive={isInteractive}
        itemHoverCardClassName={styles.stacksFacepileItemHoverCard}
      />
    ),

    stackDerivedPAPProps: stackDerivePAPProps(stack),
    actionMenuComponent: currentAccount && (
      <Actions
        stack={stack}
        chatSources={chatSources}
        iconColor="var(--dig-color__text__subtle)"
        currentAccount={currentAccount}
        setMenuOpenForStackId={setMenuOpenForStackId}
        showPinnedIcon={!augustRevision}
      />
    ),
    embeddedActionMenuComponent: currentAccount && (
      <EmbeddedActions
        stack={stack}
        onClickEdit={() => onSelectStack(stack, stackItems.length)}
        currentAccount={currentAccount}
        setMenuOpenForStackId={setMenuOpenForStackId}
      />
    ),
    onClickCard: () => onSelectStack(stack, stackItems.length),
    customColor: stack.stack_data?.custom_color,
    publishedData: stack.publish_data,
  };
};

export const convertStackSuggestionToWorkingSetCardData = (
  stack: stacks.AutoStackSuggestion,
): WorkingSetCardData => {
  return {
    title: stack.stack_data?.description ?? "",
    colorIndex: stack.stack_data?.color_index ?? 0,
    logoUrl: getStackIconUrl(stack),
    links: (stack.items ?? [])
      .filter((item) => item[".tag"] === "shortcut")
      .filter((item) => !!item.url && !!item.metadata_title)
      .map((item) => ({
        url: item.url!,
        id: item.url!,
        name: item.metadata_title!,
      })),
    actionMenuComponent: <SuggestedStackActions stack={stack} />,
    stack,
    id: "",
    namespaceId: "",
    emoji: "",
    accessLevel: "",
    isPinned: false,
    menuIsOpen: false,
    isCompanyPublishedStack: false,
    isPublicStack: false,
    memberCount: 0,
    embeddedActionMenuComponent: null,
    onClickCard: () => {},
    getMembersComponent: () => null,
    accessLevelIcon: () => <></>,
    stackDerivedPAPProps: {
      stackId: "",
      isOwner: false,
      isShared: false,
      isPinned: false,
      isAutoGenerated: false,
      isWelcomeStack: false,
      numSharedUsers: 0,
      numSections: 0,
      stackAccessLevel: "invited",
    },
  };
};

const SuggestedStackActions = ({
  stack,
}: {
  stack: stacks.AutoStackSuggestion;
}) => {
  const disableSuggestions = useDisableStackSuggestions();
  const [isLoading, setIsLoading] = useState(false);
  return (
    <Box display="flex" columnGap="8">
      <Button
        variant="filled"
        isLoading={isLoading}
        onClick={async () => {
          setIsLoading(true);
          try {
            const stackResult = await createStack({
              name: stack.stack_data?.name ?? "",
              description: "",
              colorIndex: stack.stack_data?.color_index,
              creationTypeTag: "suggested_stack",
            });
            if (stackResult.namespace_id) {
              await createStackItemsBatch(
                stackResult.namespace_id,
                (stack.items ?? [])
                  .filter((item) => item[".tag"] === "shortcut")
                  .map((item) => ({
                    url: item.url ?? "",
                    metadata_title: item.metadata_title ?? "",
                  })),
              );
            }
          } finally {
            setIsLoading(false);
          }
        }}
      >
        {i18n.t("keep_suggested_stack")}
      </Button>
      <Menu.Wrapper>
        {({ getTriggerProps, getContentProps }) => (
          <>
            <IconButton variant="borderless" {...getTriggerProps()}>
              <UIIcon src={MoreHorizontalLine} />
            </IconButton>
            <Menu.Content {...getContentProps()}>
              <Menu.Segment>
                <Menu.ActionItem
                  tone="destructive"
                  onClick={disableSuggestions}
                >
                  {i18n.t("turn_off_stack_suggestions")}
                </Menu.ActionItem>
              </Menu.Segment>
            </Menu.Content>
          </>
        )}
      </Menu.Wrapper>
    </Box>
  );
};
